import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { useQuery } from "convex/react";
import { api } from "@/smart-views/client/api";
import { isConvexConfigured } from "@/smart-views/client/convex-client";
import {
  getSmartViewIdFromPath,
  getSmartViewUpdateToastId,
  setSmartViewUnreadUpdates,
} from "@/smart-views/client/update-notifications";
import { useAppStore } from "@/store";
import { toastModule } from "@/modules/toast";
import { MDSIcon, MDSIconKind, MDSText, MDSTextVariant } from "@/mds";

const SMART_VIEW_UPDATE_TOAST_TIMEOUT_MS = 45_000;

export const SmartViewUpdateNotifier: FC = observer(function SmartViewUpdateNotifier() {
  const { store } = useAppStore();
  const updateStates = useQuery(
    api.smartViews.client.listUpdateStates,
    isConvexConfigured() ? {} : "skip"
  );
  const shownToastKeysRef = useRef(new Set<string>());
  const isDocumentVisible = useDocumentVisible();
  const visibleSmartViewIds = useMemo(() => {
    const ids = new Set<string>();

    for (const pane of store.navigation.tabs.panes) {
      const tab = pane.activeTabId ? store.navigation.tabs.tabsMap.get(pane.activeTabId) : null;
      const smartViewId = tab ? getSmartViewIdFromPath(tab.path) : null;

      if (smartViewId) {
        ids.add(smartViewId);
      }
    }

    return ids;
  }, [store.navigation.tabs.panes, store.navigation.tabs.tabsMap]);

  useEffect(() => {
    if (!updateStates) {
      return;
    }

    const unreadSmartViewIds: string[] = [];

    for (const state of updateStates) {
      if (!state.updateNotification || !state.currentVersionId) {
        continue;
      }

      const smartViewId = state.smartViewId;
      const isVisible = isDocumentVisible && visibleSmartViewIds.has(smartViewId);

      if (isVisible) {
        toast.dismiss(getSmartViewUpdateToastId(smartViewId));
        continue;
      }

      unreadSmartViewIds.push(smartViewId);

      const toastKey = `${smartViewId}:${state.currentVersionId}`;
      if (shownToastKeysRef.current.has(toastKey)) {
        continue;
      }

      shownToastKeysRef.current.add(toastKey);
      const toastId = getSmartViewUpdateToastId(smartViewId);
      toastModule.triggerToast({
        toastId,
        content: (
          <SmartViewUpdateToast
            title={state.title}
            summary={state.updateNotification.summary}
            onDismiss={event => {
              event.stopPropagation();
              toast.dismiss(toastId);
            }}
          />
        ),
        options: {
          autoClose: SMART_VIEW_UPDATE_TOAST_TIMEOUT_MS,
          pauseOnFocusLoss: false,
          pauseOnHover: false,
          onClick: () =>
            store.navigation.goToSmartView({
              smartViewId,
            }),
        },
      });
    }

    setSmartViewUnreadUpdates(unreadSmartViewIds);
  }, [isDocumentVisible, store.navigation, updateStates, visibleSmartViewIds]);

  return null;
});

const SmartViewUpdateToast: FC<{
  title: string;
  summary: string;
  onDismiss: (event: MouseEvent<HTMLButtonElement>) => void;
}> = ({ title, summary, onDismiss }) => {
  return (
    <ToastContent>
      <ToastIcon>
        <MDSIcon kind={MDSIconKind.Sparkle} size={14} />
      </ToastIcon>
      <ToastText>
        <MDSText variant={MDSTextVariant.TextS} numberOfLines={1}>
          {title} updated
        </MDSText>
        <MDSText variant={MDSTextVariant.TextXs} numberOfLines={2}>
          {summary}
        </MDSText>
      </ToastText>
      <ToastDismissButton
        aria-label="Dismiss Smart View update"
        data-smart-view-toast-dismiss
        onClick={onDismiss}
      >
        <MDSIcon kind={MDSIconKind.Exit} size={12} />
      </ToastDismissButton>
    </ToastContent>
  );
};

const ToastContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing["2"]};
  min-width: 0;
  max-width: 420px;
  padding-right: ${({ theme }) => theme.spacing["5"]};
  position: relative;

  &:hover [data-smart-view-toast-dismiss],
  [data-smart-view-toast-dismiss]:focus-visible {
    opacity: 1;
  }
`;

const ToastIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.icon.emphasis};
  padding-top: ${({ theme }) => theme.spacing["0.5"]};
`;

const ToastText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["0.5"]};
  min-width: 0;
`;

const ToastDismissButton = styled.button`
  position: absolute;
  top: -${({ theme }) => theme.spacing["1"]};
  right: -${({ theme }) => theme.spacing["1"]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: transparent;
  color: ${({ theme }) => theme.colors.icon.secondary};
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  }
`;

const useDocumentVisible = (): boolean => {
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === "visible");

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return isVisible;
};
